<script setup lang="ts">
    import SettingsPageLayout from '../components/settings/SettingsPageLayout.vue';
    import { __ } from '../stores/expressions';
    import EchoButton from '../components/EchoButton.vue';
    import { useRouter, useRoute } from 'vue-router';
    import UserDetails from '../components/users/UserDetails.vue';
    import UserAvatar from '../components/UserAvatar.vue';
    import { Ref, ref, computed } from 'vue';
    import { useConfirmationDialog } from '../stores/confirmationDialog';
    import { useSuccessDialog } from '../stores/success-dialog';
    import { updateUser, deleteUser, removeUserOverrides } from '@/js/services/api/user/userApi';
    import { useQueryGetUserDetailed, useGetMyAccount } from '@/js/hooks/user';
    import { useQueryClient } from '@tanstack/vue-query';
    import { QK } from '@/js/consts/query-keys';

    import type { RequestUpdateUser, OrgAccessSelection } from '../services/api/user/types';
    import type { AccessGroupNode } from '../services/api/role/types';
    import { CheckIcon, ChevronLeftIcon, TrashIcon, XIcon } from '@lucide/vue';

    const router = useRouter();
    const route = useRoute();
    const queryClient = useQueryClient();

    const userId = route.params.id as string;
    const { data: user } = useQueryGetUserDetailed(userId);
    const { data: myAccount } = useGetMyAccount();

    const successDialogStore = useSuccessDialog();

    type UserDetailsRef = {
        submit: () => void;
        canSubmit: boolean;
    };

    const userDetailsRef: Ref<UserDetailsRef | null> = ref(null);

    const savingUserIsLoading = ref(false);

    const fullName = computed(() => {
        if (!user.value) return '';

        const { firstName, lastName, email } = user.value;
        const name = [firstName, lastName].filter(Boolean).join(' ');
        return name || email || '';
    });

    const returnToUsers = () => {
        router.push({
            path: '/settings/manage-users',
            query: route.query,
        });
    };

    type SaveUserPayload = {
        firstName?: string;
        lastName?: string;
        email?: string;
        emailVerified?: boolean;
        roleId?: string;
        permissionsChanged?: boolean;
        permissionsOverride?: Record<string, number>;
        dataAccess?: OrgAccessSelection;
        dataAccessChanged?: boolean;
        orgDataAccess?: AccessGroupNode[];
    };

    const saveUser = async (userData: SaveUserPayload) => {
        if (!user.value) return;

        const payload: RequestUpdateUser = {
            userId: user.value.id,
        };

        if (userData.firstName !== undefined) payload.firstName = userData.firstName;
        if (userData.lastName !== undefined) payload.lastName = userData.lastName;
        if (userData.email) payload.email = userData.email;
        if (userData.roleId) payload.roleId = userData.roleId;
        if (
            userData.emailVerified !== undefined &&
            userData.emailVerified !== (user.value.emailVerified ?? false)
        ) {
            payload.emailVerified = userData.emailVerified;
        }

        // Overrides ride along on the user-update call so the role change and the override land in one transaction.
        // All three sections go in full; the backend stores only what diverges from the role, so one section can't wipe another.
        // Without dataAccess the user's setting is unknown, and defaulting it here would store a narrowing override.
        if ((userData.permissionsChanged || userData.dataAccessChanged) && userData.dataAccess) {
            payload.access = {
                permissions: userData.permissionsOverride ?? {},
                dataAccess: userData.dataAccess,
                orgDataAccess: userData.orgDataAccess ?? [],
            };
        }

        savingUserIsLoading.value = true;
        try {
            await updateUser(payload);

            await queryClient.invalidateQueries({ queryKey: [QK.USERS.DETAILED, user.value.id] });

            // If you edited your own account, refresh my-account too so usePermissions
            // (UI/permission-gating) reflects any changed permissions without a hard refresh.
            if (myAccount.value?.id === user.value.id) {
                await queryClient.invalidateQueries({ queryKey: [QK.ACCOUNT.GET] });
            }
        } finally {
            savingUserIsLoading.value = false;
        }

        await successDialogStore.openDialog({
            message: __('update-user-success-message', {
                name: `${userData.firstName} ${userData.lastName}`,
            }),
        });
    };

    const submitForm = () => {
        userDetailsRef.value?.submit();
    };

    const cancel = () => {
        returnToUsers();
    };

    const confirm = useConfirmationDialog();

    const confirmDeleteUser = async () => {
        if (!user.value) return;

        const confirmed = await confirm.openDialog({
            title: __('confirm-delete-user-title'),
            message: __('confirm-delete-user-message', {
                name: `${user.value.firstName} ${user.value.lastName}`,
            }),
            confirmText: __('yes-confirm-delete'),
            cancelText: __('no'),
            confirmIcon: TrashIcon,
        });

        if (!confirmed) return;
        await deleteUser(user.value.id);
        returnToUsers();
    };

    const confirmResetOverrides = async () => {
        if (!user.value) return;

        const name = `${user.value.firstName} ${user.value.lastName}`.trim() || user.value.email;

        const confirmed = await confirm.openDialog({
            title: __('confirm-reset-user-overrides-title'),
            message: __('confirm-reset-user-overrides-message', { name }),
            confirmText: __('yes-confirm-reset'),
            cancelText: __('no'),
            confirmIcon: CheckIcon,
        });

        if (!confirmed) return;
        await removeUserOverrides(user.value.id);

        await queryClient.invalidateQueries({ queryKey: [QK.USERS.DETAILED, user.value.id] });
        if (myAccount.value?.id === user.value.id) {
            await queryClient.invalidateQueries({ queryKey: [QK.ACCOUNT.GET] });
        }

        await successDialogStore.openDialog({
            message: __('reset-user-overrides-success-message', { name }),
        });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('edit-user') }}</h1>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="!userDetailsRef?.canSubmit"
                :loading="savingUserIsLoading"
                @click="submitForm"
            >
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="cancel">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <EchoButton
                variant="tertiary"
                :route="{ path: '/settings/manage-users', query: route.query }"
            >
                <template #icon-left><ChevronLeftIcon /></template>
                {{ __('back-to-list') }}
            </EchoButton>

            <div class="my-10 flex items-center gap-4">
                <UserAvatar :label="fullName" size="xl" color="avocado" />
                <p class="heading-lg text-primary-beet">
                    {{ fullName }}
                </p>
            </div>
            <UserDetails
                v-if="user"
                ref="userDetailsRef"
                :user="user"
                @save="saveUser"
                @reset-overrides="confirmResetOverrides"
            />
            <div class="mt-10 pt-6 border-t border-gray-lightest">
                <EchoButton variant="danger" @click="confirmDeleteUser">
                    {{ __('delete-user') }}
                    <template #icon-right><TrashIcon /></template>
                </EchoButton>
            </div>
        </template>
    </SettingsPageLayout>
</template>
